import { useState } from 'react';

import useConfig from 'hooks/useConfig';
import useGetAxiosInstance from 'hooks/useGetAxiosInstance';
import { CuredApiPaths, DownloadQueryKeys, ApiMethods } from 'models/enums';
import { PathWithID } from 'models/types';
import { FiltersForApiState } from 'pages/InsightsPage/dashboards/filtering/types';
import { getErrorWithMessage } from 'utils';

type Props = {
    filtersForApi?: FiltersForApiState;
};

const isPathWithID = (path: CuredApiPaths | PathWithID | string): path is PathWithID =>
    Boolean((path as PathWithID)?.path);

/**
 * A custom wrapper hook around react-query for handling file downloads
 * @param queryKey A unique key for react-query caching
 * @param path API path to download from
 * @param requestMethod The HTTP method to use (GET or POST)
 * @returns Object containing download function and loading state
 */
const useDownloadQuery = (
    queryKey: DownloadQueryKeys,
    path: CuredApiPaths | string,
    requestMethod: ApiMethods = ApiMethods.GET
) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { config } = useConfig();
    const getApiInstance = useGetAxiosInstance(ApiMethods.GET);
    const postApiInstance = useGetAxiosInstance(ApiMethods.POST);

    /**
     * Triggers the file download
     * @param options Optional request options that include filters
     */
    const resetDownloadState = () => {
        setIsSuccess(false);
        setError(null);
    };

    const downloadFile = async (options?: Props) => {
        if (isDownloading) return;

        resetDownloadState();
        setIsDownloading(true);
        try {
            const filters = options?.filtersForApi || {};

            // Use only the provided request method to determine which API instance to use
            const usePostMethod = requestMethod === ApiMethods.POST;

            // Get appropriate API instance based on method
            const apiInstance = await (usePostMethod ? postApiInstance() : getApiInstance());

            // Build URL
            const url = isPathWithID(path) ? `${config.API_URL}${path.url}` : path;

            // Make the API call
            const response = usePostMethod
                ? await apiInstance.post(url, filters, { responseType: 'blob' })
                : await apiInstance.get(url, { responseType: 'blob' });

            if (!response.data) throw new Error('No data received');

            // Create download link and trigger download
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;

            // Get filename from headers or use default
            const contentDisposition = response.headers?.['content-disposition'] as string;
            const filename = contentDisposition?.includes('filename=')
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                : 'download.zip';

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);

            setIsSuccess(true);
        } catch (error) {
            const errorWithMessage = getErrorWithMessage(error);
            setError(errorWithMessage);
            throw errorWithMessage;
        } finally {
            setIsDownloading(false);
        }
    };

    return {
        downloadFile,
        isDownloading,
        isSuccess,
        error,
        resetDownloadState,
    };
};

export default useDownloadQuery;
